import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import * as Sentry from '@sentry/browser';
import { CaptureContext } from '@sentry/core';

import { SentryOptions } from '../../interfaces';
import { jsConfig } from '../config/jsConfig';
import { CAPTURE_MESSAGE, SentryLogger } from './sentry.logger';
import { isPresent } from '../../utils';

const SENTRY_ENABLED = !!jsConfig.sentryDsn && typeof window !== 'undefined';
const SENTRY_LOG_TO_CONSOLE = ['local', 'test'].includes(jsConfig.sentryEnv);
const CHUNKLOADERROR = /Loading chunk [\da-z\-_]+ failed/;
const CHUNKLOADERROR_NAME = 'ChunkLoadError_ForceReload';
const ONEHOUR = 1000 * 60 * 60; // One hour in ms

export const captureException = (error: unknown): void => {
  const isChunkLoadError = error instanceof Error && CHUNKLOADERROR.test(error.message) && !hasCurrentlyForceReloaded();

  if (isChunkLoadError) {
    console.error(error);
    // Handles ChunkLoadError by forcing reload, but only once per hour to avoid reload loop
    setReloadTimestamp();
    window.location.reload();
    return;
  }

  if (SENTRY_ENABLED) {
    Sentry.captureException(error, {
      extra: getExtra(error),
      fingerprint: error instanceof HttpErrorResponse ? [`dedupe-${error.error?.timestamp}`] : undefined,
    });
  }

  if (!SENTRY_ENABLED || SENTRY_LOG_TO_CONSOLE) {
    console.error(error);
  }
};

export function captureMessage(message: string, options: SentryOptions): void {
  const context: CaptureContext = {
    level: options.level,
    extra: getExtra(options.extra || {}),
  };

  if (SENTRY_ENABLED) {
    Sentry.captureMessage(message, context);
  }

  if (!SENTRY_ENABLED || SENTRY_LOG_TO_CONSOLE) {
    (console[options.level] || console.log)(message, context);
  }
}

export function shouldIncludeUserAgent(userAgent: string): boolean {
  const isOldIe = /MSIE [5678]\./.test(userAgent);
  const isOldAndroid = /Android [234]\.]/.test(userAgent) && !/Chrome/.test(userAgent);
  return !(isOldIe || isOldAndroid);
}

const sanitizeData = (value) => {
  if (typeof value === 'string') {
    return value.replace(/(hash|cartId|orderId)=(\w+(==)?)/g, '$1=********');
  } else if (Array.isArray(value)) {
    return value.map(sanitizeData);
  } else if (typeof value === 'object' && value !== null) {
    return sanitizeObject(value);
  } else {
    return value;
  }
};

const sanitizeKeys = ['hash', 'cartId', 'orderId'];

// Exposed for testing
export const sanitizeObject = (input: Record<string, unknown>): Record<string, unknown> => {
  return Object.keys(input).reduce((all, key) => {
    all[key] = sanitizeKeys.indexOf(key) !== -1 ? '********' : sanitizeData(input[key]);
    return all;
  }, {});
};

/**
 * Set session storage item with unix timestamp of time that force reload was performed
 */
const setReloadTimestamp = () => {
  sessionStorage.setItem(CHUNKLOADERROR_NAME, new Date().getTime().toString());
};

const getReloadTimestamp = () => {
  return sessionStorage.getItem(CHUNKLOADERROR_NAME) ? parseInt(sessionStorage.getItem(CHUNKLOADERROR_NAME), 10) : null;
};

const hasCurrentlyForceReloaded = () => {
  const nowMinusHour = new Date().getTime() - ONEHOUR;
  return getReloadTimestamp() && new Date(getReloadTimestamp()).getTime() > nowMinusHour;
};

const getHeaders = (headers: unknown): { headers?: Record<string, string> } => {
  if (!(headers instanceof HttpHeaders)) {
    return {};
  }
  return {
    headers: headers.keys().reduce<Record<string, string>>((acc, key) => {
      const values = headers.getAll(key);
      if (values) {
        acc[key] = values.join(',');
      }
      return acc;
    }, {}),
  };
};

const getExtra = (extra: unknown) => {
  if (!extra || typeof extra !== 'object') {
    return {};
  }

  if (extra instanceof HttpErrorResponse) {
    return {
      ...extra,
      ...getHeaders(extra.headers || {}),
    };
  }

  return Object.entries(extra).reduce((acc, [key, value]) => {
    if (value instanceof HttpErrorResponse) {
      return {
        ...acc,
        ...value,
        ...getHeaders(value.headers || {}),
      };
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {});
};

const normalizeUrl = (
  rawUrl: string
): { domain: string; normalizedPath: string; queryParams: Record<string, string> } => {
  try {
    const parsedUrl = new URL(rawUrl);

    const normalizedPath = parsedUrl.pathname
      .replace(/\b\d+\b/g, ':id')
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, ':uuid')
      .replace(/\b(?=[a-zA-Z]*\d)(?=\d*[a-zA-Z])[a-zA-Z0-9]{6,}\b/g, ':code')
      .replace(/\b[A-Z]{6}\b/g, ':code')
      .replace(/\/+/g, '/')
      .replace(/\/+$/, '');

    const queryParams: Record<string, string> = Array.from(parsedUrl.searchParams.entries()).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {}
    );

    return { domain: parsedUrl.hostname, normalizedPath, queryParams };
  } catch {
    return { domain: '', normalizedPath: rawUrl, queryParams: {} };
  }
};

const normalizeMessage = (
  input: string | HttpErrorResponse
): {
  message: string;
  queryParams: Record<string, string>;
} => {
  if (!isPresent(input)) {
    return { message: '', queryParams: {} };
  }

  const isHttpError = input instanceof HttpErrorResponse;
  const extractedUrl: string | undefined = isHttpError ? (input.url ?? undefined) : undefined;
  const rawMessage: string = isHttpError ? (input.message ?? 'unknown') : input;
  const url: string | undefined = extractedUrl ?? rawMessage.match(/https?:\/\/[^\s)'"<>]+/)?.[0] ?? undefined;

  if (url) {
    const status: string | undefined = isHttpError ? input.status?.toString() : undefined;
    const statusText: string | undefined = isHttpError ? input.statusText : undefined;
    const eventName: string = isHttpError ? (input.error?.key ?? 'HTTP_ERROR') : 'EVENT_ERROR';

    const { domain, normalizedPath, queryParams } = normalizeUrl(url);

    const processedMessage =
      domain && normalizedPath ? `${eventName} ${status} ${statusText} at ${domain}${normalizedPath}` : rawMessage;

    return { message: processedMessage, queryParams };
  }

  return { message: rawMessage, queryParams: {} };
};

const handleBeforeSend = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
  if (!loggingIsAllowedForEvent(event, hint)) {
    return null;
  }

  const fixedRequest = { ...event.request, url: event.request?.url.split('#')[0] };

  const exception = hint?.originalException;
  const isHttpException = exception && exception instanceof HttpErrorResponse;

  const { message, queryParams } = normalizeMessage(isHttpException ? exception : (event.message ?? ''));
  const includeQueryParams = Object.keys(queryParams).length ? { queryParams } : {};
  const fingerprints = event.fingerprint?.filter((f) => !f.startsWith('dedupe-')) ?? [];
  const { __serialized__, ...extra } = event.extra || {};
  const processedEvent: Sentry.ErrorEvent = {
    ...event,
    ...(message ? { message } : {}),
    request: fixedRequest,
    extra: {
      ...extra,
      ...includeQueryParams,
    },
    exception: isHttpException ? undefined : event.exception,
    fingerprint: fingerprints.length > 0 ? fingerprints : undefined,
    tags: {
      ...event.tags,
      'session.id': sessionStorage.getItem('finnaircom.X-Session-Id') || 'N/A',
    },
  };
  return sanitizeObject(processedEvent as unknown as Record<string, unknown>) as unknown as Sentry.ErrorEvent;
};

// This code should be executed in main.ts to ensure Sentry is set up before the application bootstrap
export const initSentry = (): void => {
  if (SENTRY_ENABLED) {
    Sentry.init({
      dsn: jsConfig.sentryDsn,
      release: `${jsConfig.sentryPrefix}${jsConfig.buildEnv}-${jsConfig.versionInfo.releaseGitCommitHash}`,
      environment: jsConfig.sentryEnv,
      sendDefaultPii: true,
      allowUrls: [
        // To get sentry working locally
        /webpack-internal/,
        // MATCH Content-Security-Policy here
        /\.finnair\.com/,
        /\.googletagmanager\.com/,
        /\.google-analytics\.com/,
      ],
      normalizeDepth: 4,
      ignoreErrors: [
        '[object Object]',
        'Navigator.geolocation fetch failed',
        "Failed to execute 'sendBeacon' on 'Navigator': Refused to send beacon",
        /(?=.*QuotaExceededError)(?=.*snapinsPage_).*/, // Caused by Fluido/Salesforce chat and flooding from bots
      ],
      beforeSend: handleBeforeSend,
    });
  }
};

const getStatus = (event: Sentry.Event, hint: Sentry.EventHint): number | undefined => {
  const extraStatus = event?.extra?.status;
  if (typeof extraStatus === 'number') {
    return extraStatus;
  }
  const errorStatus = (event?.extra?.error as HttpErrorResponse)?.status;
  const hintStatus = (hint?.originalException as HttpErrorResponse)?.status;
  return errorStatus ?? hintStatus;
};

export function loggingIsAllowedForEvent(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  if (!SENTRY_ENABLED) {
    return false;
  }

  if (!shouldIncludeUserAgent(window.navigator.userAgent)) {
    return false;
  }

  if (event.request?.url && !isSentryAllowedForDomain(event.request.url)) {
    return false;
  }

  const status = getStatus(event, hint);

  // Filter out http 0 errors
  return status !== 0;
}

export const isSentryAllowedForDomain = (url: string): boolean =>
  /^http(s)?:\/\/www(-(dev|master|preprod|staging))?\.finnair\.com/.test(url);

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    captureException((error as { originalError?: unknown })?.originalError || error);
  }
}

@NgModule({
  providers: [{ provide: CAPTURE_MESSAGE, useValue: captureMessage }, SentryLogger],
})
export class SentryModule {}
